import { ChangeDetectionStrategy, Component, HostListener, input, model, output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { CoordinateInputComponent, WhButtonComponent } from '@wheere-front/shared-ui';

export type PositionDialogMode = 'gateway' | 'tracker' | 'antenna';

export interface PositionResult {
  lat: number;
  lng: number;
  altitude: number;
  antennaHeight?: number;
}

@Component({
  selector: 'lib-position-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, CoordinateInputComponent, WhButtonComponent, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <div *transloco="let t" class="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/40 dark:bg-black/60" (click)="onCancel()"></div>

        <div
          class="relative flex max-h-[90vh] w-full max-w-[750px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800">
          <!-- Header -->
          <div
            class="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3.5 dark:border-slate-700">
            <h3 class="text-base font-semibold text-gray-900 dark:text-slate-100">{{ t(dialogTitle()) }}</h3>
            <button
              type="button"
              class="inline-flex size-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-300"
              [attr.aria-label]="t('siteConfiguration.positionDialog.close')"
              (click)="onCancel()">
              <i class="pi pi-times text-sm"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="flex-1 overflow-y-auto px-5 py-4">
            <form [formGroup]="form" class="flex flex-col gap-3">
              <!-- Coordinates -->
              <lib-coordinate-input
                #coordinateInput
                formControlName="position"
                [lat]="form.value.position?.lat"
                [lng]="form.value.position?.lng">
              </lib-coordinate-input>

              <!-- Altitude -->
              <div
                class="grid gap-3"
                [class.grid-cols-1]="mode() !== 'tracker'"
                [class.sm:grid-cols-2]="mode() === 'tracker'">
                <div>
                  <label for="posAltitude" class="mb-1 block text-sm font-medium text-gray-600 dark:text-slate-300">
                    {{
                      mode() === 'tracker'
                        ? t('siteConfiguration.positionDialog.trackerAltitude')
                        : t('siteConfiguration.positionDialog.altitude')
                    }}
                  </label>
                  <input
                    id="posAltitude"
                    type="number"
                    formControlName="altitude"
                    [placeholder]="t('siteConfiguration.positionDialog.altitudePlaceholder')"
                    step="any"
                    [min]="-9000"
                    [max]="9000"
                    class="focus:border-primary-500 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none transition-colors dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100" />
                </div>

                @if (mode() === 'tracker') {
                  <div>
                    <label
                      for="antennaHeight"
                      class="mb-1 block text-sm font-medium text-gray-600 dark:text-slate-300"
                      >{{ t('siteConfiguration.positionDialog.antennaAltitude') }}</label
                    >
                    <input
                      id="antennaHeight"
                      type="number"
                      formControlName="antennaHeight"
                      [placeholder]="t('siteConfiguration.positionDialog.antennaAltitudePlaceholder')"
                      step="any"
                      [min]="-9000"
                      [max]="9000"
                      class="focus:border-primary-500 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none transition-colors dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100" />
                    @if (antennaDiffTooHigh) {
                      <div class="mt-1.5 flex items-center gap-1.5 text-xs text-red-500 dark:text-red-400">
                        <i class="pi pi-exclamation-triangle text-xs"></i>
                        {{ t('siteConfiguration.positionDialog.diffTooHigh') }}
                      </div>
                    }
                  </div>
                }
              </div>
            </form>
          </div>

          <!-- Footer -->
          <div
            class="flex flex-col gap-2 border-t border-gray-100 px-5 py-3.5 sm:flex-row sm:justify-end dark:border-slate-700">
            <wh-button variant="outline" userClass="w-full sm:w-auto" (click)="onCancel()">
              <i class="pi pi-times"></i>
              {{ t('siteConfiguration.cancel') }}
            </wh-button>
            <wh-button userClass="w-full gap-2 sm:w-auto" [disabled]="form.invalid" (click)="onSave()">
              <i class="pi pi-check"></i>
              {{ t('siteConfiguration.positionDialog.save') }}
            </wh-button>
          </div>
        </div>
      </div>
    }
  `,
})
export class PositionDialogComponent {
  mode = input<PositionDialogMode>('gateway');
  visible = model<boolean>(false);

  positionSaved = output<PositionResult>();

  @ViewChild('coordinateInput') coordinateInput?: CoordinateInputComponent;

  form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      position: this.fb.control<{ lat: number; lng: number }>(
        { lat: undefined!, lng: undefined! },
        { validators: Validators.required }
      ),
      altitude: [undefined, Validators.required],
      antennaHeight: [undefined],
    });
  }

  /** Clé transloco du titre, selon ce qu'on positionne. */
  dialogTitle(): string {
    switch (this.mode()) {
      case 'gateway':
        return 'siteConfiguration.positionDialog.titleGateway';
      case 'tracker':
        return 'siteConfiguration.positionDialog.titleTracker';
      case 'antenna':
        return 'siteConfiguration.positionDialog.titleAntenna';
    }
  }

  get antennaDiffTooHigh(): boolean {
    const altitude = this.form.get('altitude')?.value;
    const antennaHeight = this.form.get('antennaHeight')?.value;
    return (
      altitude != null &&
      antennaHeight != null &&
      !isNaN(altitude) &&
      !isNaN(antennaHeight) &&
      antennaHeight - altitude > 2
    );
  }

  patchValues(
    lat: number | undefined,
    lng: number | undefined,
    altitude: number | undefined,
    antennaHeight?: number | undefined
  ): void {
    this.form.patchValue({
      position: { lat: lat!, lng: lng! },
      altitude,
      antennaHeight: antennaHeight ?? undefined,
    });
  }

  onSave(): void {
    if (this.form.invalid) return;

    let coordinates;
    if (this.coordinateInput) {
      coordinates = this.coordinateInput.forceCalculateAndGetValue();
    } else {
      coordinates = this.form.value.position;
    }

    const result: PositionResult = {
      lat: coordinates.lat,
      lng: coordinates.lng,
      altitude: this.form.value.altitude,
    };

    if (this.mode() === 'tracker') {
      result.antennaHeight = this.form.value.antennaHeight;
    }

    this.positionSaved.emit(result);
    this.visible.set(false);
    this.form.reset();
  }

  onCancel(): void {
    this.visible.set(false);
    this.form.reset();
  }

  /** Escape key — mirrors the old p-dialog closeOnEscape. No-op when closed. */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.visible()) this.onCancel();
  }
}
